/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio (U33); os rótulos vão por `msg` */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';

// Listas fechadas dos formulários da barra, na ordem do legado e pelo valor técnico (U33).

export interface BarOption {
  readonly value: string;
  readonly label: MessageDescriptor;
}

/**
 * "Novo Status" do "Definir status" (`optionsNewStatus`, creditNoteOptions.ts:88-174), já na ordem de
 * `sortToSelectOptionList` (ArrayFormats.ts:10-28: rótulo pt-BR em maiúsculas, comparação por código).
 */
export const MANUAL_UPDATE_STATUSES: readonly BarOption[] = [
  { value: 'WaitLiquidation', label: msg`Aguardando Liberação para Liquidação` },
  { value: 'VehicleInspectionApproval', label: msg`Aprovação da Vistoria Veicular` },
  { value: 'ComplianceApproval', label: msg`Aprovação de Compliance` },
  { value: 'CreditApproval', label: msg`Aprovação de Crédito` },
  { value: 'InstrumentApproval', label: msg`Aprovação de Instrumento` },
  { value: 'Canceled', label: msg`Cancelada` },
  { value: 'Signatures', label: msg`Coleta de assinaturas` },
  { value: 'Finished', label: msg`Encerrado` },
  { value: 'Warranty', label: msg`Garantia` },
  { value: 'ManualWarranty', label: msg`Garantia Manual` },
  { value: 'ManualLiquidation', label: msg`Liquidação manual` },
  { value: 'Liquidation', label: msg`Pago` },
  { value: 'Draft', label: msg`Rascunho` },
  { value: 'Disapproved', label: msg`Reprovado` },
  { value: 'MarginReserveApproval', label: msg`Reserva de Margem` },
  { value: 'Revision', label: msg`Revisão` },
  { value: 'WarrantyRevision', label: msg`Revisão de Garantia` },
  { value: 'PaymentRevision', label: msg`Revisão de Pagamento` },
  { value: 'PartnerSignaturesValidation', label: msg`Validação de assinatura pelo parceiro` },
  { value: 'SignaturesValidation', label: msg`Validação de Assinaturas` },
  { value: 'VehicleInspection', label: msg`Vistoria Veicular` },
];

/** "Selecione a etapa da linha do tempo" (`optionsNewTimeline`, creditNoteOptions.ts:4-86), sem ordenar. */
export const MANUAL_UPDATE_TIMELINE: readonly BarOption[] = [
  { value: 'CreditApprove', label: msg`Aprovação de Crédito` },
  { value: 'ComplianceApprove', label: msg`Aprovação de Compliance` },
  { value: 'InstrumentApprove', label: msg`Aprovação de Instrumento` },
  { value: 'PartnerSignaturesValidation', label: msg`Validação de assinatura pelo parceiro` },
  { value: 'SignaturesValidation', label: msg`Validação de Assinaturas` },
  { value: 'Signatures', label: msg`Coleta de assinaturas` },
  { value: 'Finished', label: msg`Encerrado` },
  { value: 'Expired', label: msg`Expirada` },
  { value: 'Cancel', label: msg`Cancelada` },
  { value: 'Liquidation', label: msg`Pago` },
  { value: 'PaymentRevision', label: msg`Revisão de Pagamento` },
  { value: 'Draft', label: msg`Rascunho` },
  { value: 'Disapproved', label: msg`Reprovado` },
  { value: 'Revision', label: msg`Revisão` },
  { value: 'WarrantyRevision', label: msg`Revisão de Garantia` },
  { value: 'Warranty', label: msg`Garantia` },
  { value: 'ManualWarranty', label: msg`Garantia Manual` },
  { value: 'MarginReserveApproval', label: msg`Reserva de Margem` },
  { value: 'VehicleInspection', label: msg`Vistoria Veicular` },
  { value: 'VehicleInspectionApproval', label: msg`Aprovação da Vistoria Veicular` },
];

/** "Atualizar linha do tempo" e "Encerrar esteira" (`manualUpdateOptions`, creditNoteOptions.ts:176-180). */
export const MANUAL_UPDATE_ACTIONS: readonly BarOption[] = [
  { value: 'None', label: msg`Não` },
  { value: 'EndCurrent', label: msg`Encerrar etapa atual` },
  { value: 'EndAndCreateNew', label: msg`Encerrar etapa atual e iniciar nova` },
];
export type ManualUpdateAction = 'None' | 'EndCurrent' | 'EndAndCreateNew';

/** "Novo Status" do roteiro (`closureStatusOptions`, LiquidationScheduleClosureUpdateStatusSchema.ts:19-23): número no fio. */
export const CLOSURE_STATUSES: readonly { readonly value: 0 | 5 | 6; readonly label: MessageDescriptor }[] = [
  { value: 0, label: msg`Pendente` },
  { value: 5, label: msg`Cancelado` },
  { value: 6, label: msg`Finalizado` },
];

/** "Tipo de permissão" (ShareRecord.tsx:26-30). */
export const PRINCIPAL_TYPES: readonly BarOption[] = [
  { value: 'User', label: msg`Usuário` },
  { value: 'Group', label: msg`Grupo` },
  { value: 'Tenant', label: msg`Tenant` },
];

/** "Nível de permissão" (ShareRecord.tsx:52-55). */
export const PERMISSION_LEVELS: readonly BarOption[] = [
  { value: 'View', label: msg`Visualizar` },
  { value: 'Edit', label: msg`Editar` },
];

/** "Sistema de averbação" do token (ReserveToken.tsx:20-29). */
export const TOKEN_OFFICES: readonly BarOption[] = [
  { value: 'Fgts', label: msg`FGTS` },
  { value: 'DataPrevCreditoTrabalhador', label: msg`Dataprev - Crédito do Trabalhador` },
  { value: 'Zetra', label: msg`Zetra` },
  { value: 'Siape', label: msg`SIAPE` },
  { value: 'Others', label: msg`Outros` },
  { value: 'CarEquity', label: msg`Car Equity` },
];

/** "Tipo do token" (ReserveToken.tsx:35-40). */
export const TOKEN_TYPES: readonly BarOption[] = [
  { value: 'Reserve', label: msg`Reserva` },
  { value: 'NestEgg', label: msg`Pecúlio` },
  { value: 'Warranty', label: msg`Averbação` },
];

/** `attributesTypeFormat` (helpers/formats/StringFormats.ts:12-21): o rótulo de cada restrição de compliance. */
export const RESTRICTION_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  email: msg`Email`,
  zipcode: msg`CEP`,
  documentnumber: msg`RG`,
  phone: msg`Telefone`,
  ispb: msg`ISPB`,
};

/** Categorias do produto que escondem "Cancelar garantias" (DoneRevisionForm.tsx:92-95). */
export const HIDE_CANCEL_WARRANTIES = ['FGTS', 'CAR EQUITY', 'FINANCIAMENTO DE VEICULO'] as const;

/** Mensagens do carnê por status HTTP (`mapDownloadCarneError`, installment.helpers.ts:4-28). */
export const CARNE_ERRORS = {
  422: msg`Não foi possível gerar o carnê: as parcelas ainda não possuem cobrança emitida.`,
  404: msg`Parcelamento não encontrado.`,
  unexpected: msg`Erro ao gerar o carnê. Tente novamente em alguns instantes.`,
} as const;
